import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, Settings, ShoppingCart, Headphones, UserCircle, LogOut, ChevronDown, Briefcase } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface EffectiveModule {
  isInternal: boolean;
  path: string;
  label: string;
}

interface UserMenuPopoverProps {
  isAdmin: boolean;
  cartEnabled: boolean;
  onSignOut: () => void;
  effectiveModule: EffectiveModule | null;
}

export function UserMenuPopover({ isAdmin, cartEnabled, onSignOut, effectiveModule }: UserMenuPopoverProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const handleLinkClick = () => setOpen(false);
  const handleSignOut = () => {
    setOpen(false);
    onSignOut();
  };

  const isInternal = !!effectiveModule;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex h-10 items-center justify-center rounded-md px-2 xl:px-3 py-2 text-sm font-medium",
            "bg-transparent text-foreground/80 transition-colors",
            "hover:text-primary hover:bg-accent",
            "focus:outline-none focus:ring-2 focus:ring-primary/20",
            open && "bg-accent text-primary"
          )}
        >
          <User className="h-4 w-4 mr-2" />
          {isInternal ? (effectiveModule?.label || "Menu") : t("nav.clientArea")}
          <ChevronDown className={cn(
            "ml-1 h-3 w-3 transition-transform duration-200",
            open && "rotate-180"
          )} />
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[220px] p-0 border-border"
        align="end"
        sideOffset={8}
      >
        <ul className="p-2 bg-popover rounded-lg">
          {isInternal ? (
            <>
              {isAdmin && (
                <li>
                  <Link 
                    to="/admin" 
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="font-bold">{t("nav.adminPanel")}</span>
                  </Link>
                </li>
              )}
              {effectiveModule && (
                <li>
                  <Link 
                    to={effectiveModule.path} 
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent hover:text-primary transition-colors group"
                  >
                    <Briefcase className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    <span className="font-bold">{effectiveModule.label}</span>
                  </Link>
                </li>
              )}
              <li>
                <Link 
                  to="/conta?tab=profile" 
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent hover:text-primary transition-colors group"
                >
                  <UserCircle className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="font-bold">Perfil</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/assistencia" 
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent hover:text-primary transition-colors group"
                >
                  <Headphones className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="font-bold">{t("nav.support")}</span>
                </Link>
              </li>
              {cartEnabled && (
                <li>
                  <Link 
                    to="/carrinho" 
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent hover:text-primary transition-colors group"
                  >
                    <ShoppingCart className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    <span className="font-bold">{t("nav.cart")}</span>
                  </Link>
                </li>
              )}
              <li className="border-t border-border mt-2 pt-2">
                <button 
                  onClick={handleSignOut} 
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-destructive/10 text-destructive transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="font-bold">{t("nav.logout")}</span>
                </button>
              </li>
            </>
          ) : (
            <>
              {isAdmin && (
                <li>
                  <Link 
                    to="/admin" 
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="font-bold">{t("nav.adminPanel")}</span>
                  </Link>
                </li>
              )}
              <li>
                <Link 
                  to="/conta" 
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent hover:text-primary transition-colors group"
                >
                  <UserCircle className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="font-bold">{t("nav.myAccount")}</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/assistencia" 
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent hover:text-primary transition-colors group"
                >
                  <Headphones className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="font-bold">{t("nav.support")}</span>
                </Link>
              </li>
              {cartEnabled && (
                <li>
                  <Link 
                    to="/carrinho" 
                    onClick={handleLinkClick}
                    className="flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-accent hover:text-primary transition-colors group"
                  >
                    <ShoppingCart className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    <span className="font-bold">{t("nav.cart")}</span>
                  </Link>
                </li>
              )}
              <li className="border-t border-border mt-2 pt-2">
                <button 
                  onClick={handleSignOut} 
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md hover:bg-destructive/10 text-destructive transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="font-bold">{t("nav.logout")}</span>
                </button>
              </li>
            </>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
